/* eslint-disable no-unused-vars */
import React from 'react';
import PropTypes from 'prop-types';
import isArray from 'lodash/isArray';
import find from 'lodash/find';
import { Row, Col } from 'antd';
import FormBuilderField from './formbuilderfield';

const widgetRegistry = {};

// ------------------ Utility Functions ------------------

function getWidget(widget) {
  if (!widget) return null;
  if (typeof widget === 'string') {
    if (!widgetRegistry[widget] || !widgetRegistry[widget].widget) {
      throw new Error(
        `Widget '${widget}' not found, did you define it using FormBuilder.defineWidget()?`
      );
    }
    return widgetRegistry[widget].widget;
  }
  return widget;
}

function normalizeMeta(meta) {
  let fields = isArray(meta) ? meta : meta.fields || meta.elements;
  if (!fields) fields = [meta];

  fields = fields.map((field) => {
    const widget = getWidget(field.widget);
    const viewWidget = getWidget(field.viewWidget);
    const dynamic = field.dynamic !== false;

    const registryItem = find(
      Object.values(widgetRegistry),
      entry => (entry.widget === widget || entry.widget === viewWidget) && entry.metaConvertor
    );

    if (registryItem) {
      const newField = registryItem.metaConvertor(field);
      if (!newField) {
        throw new Error(`metaConvertor of '${String(field.widget)}' must return a field`);
      }
      return { ...newField, viewWidget, widget, dynamic };
    }

    return { ...field, widget, viewWidget, dynamic };
  });

  return isArray(meta) || (!meta.fields && !meta.elements)
    ? { fields }
    : { ...meta, fields };
}

// ------------------ FormBuilder Component ------------------

function FormBuilder(props) {
  const { getMeta, form } = props;
  const meta = getMeta ? getMeta(form, props) : props.meta;
  return (
    <FormBuilderInner
      {...props}
      form={form?.current || form || null}
      meta={meta}
    />
  );
}

function FormBuilderInner(props) {
  const {
    meta,
    viewMode,
    initialValues,
    disabled = false,
    form = null
  } = props;

  if (!meta) return null;

  const normalizedMeta = normalizeMeta(meta);
  const finalMeta = {
    ...normalizedMeta,
    viewMode: normalizedMeta.viewMode || viewMode,
    initialValues: normalizedMeta.initialValues || initialValues
  };

  const {
    fields,
    columns = 1,
    gutter = 10
  } = finalMeta;

  const elements = fields.map((field, index) => (
    <FormBuilderField
      key={field.key || `field-${index}`}
      field={field}
      disabled={disabled}
      meta={finalMeta}
      form={form}
    />
  ));

  if (columns === 1) {
    return <>{elements}</>;
  }

  const rows = [];
  const spanUnit = 24 / columns;

  for (let i = 0; i < elements.length;) {
    const cols = [];

    for (
      let j = 0;
      (j < columns || j === 0) &&
      i < elements.length &&
      (!['left', 'both'].includes(fields[i].clear) || j === 0);
    ) {
      const fieldSpan = Math.min(columns, fields[i].colSpan || 1);
      const effectiveSpan = Math.min(24, spanUnit * fieldSpan);

      cols.push(
        <Col key={j} span={effectiveSpan}>
          {elements[i]}
        </Col>
      );

      j += fieldSpan;

      if (['both', 'right'].includes(fields[i].clear)) {
        i += 1;
        break;
      }

      i += 1;
    }

    rows.push(
      <Row key={`row-${i}`} gutter={gutter}>
        {cols}
      </Row>
    );
  }

  return <>{rows}</>;
}

// ------------------ Widget API ------------------

FormBuilder.defineWidget = (name, widget, metaConvertor = null) => {
  if (widgetRegistry[name]) {
    throw new Error(`Widget "${name}" is already defined.`);
  }
  widgetRegistry[name] = { widget, metaConvertor };
};

// ------------------ Utility Hook ------------------

FormBuilder.useForceUpdate = () => {
  const [, updateState] = React.useState();
  const forceUpdate = React.useCallback(() => updateState({}), []);
  return forceUpdate;
};

// ------------------ PropTypes ------------------

FormBuilder.propTypes = {
  meta: PropTypes.any,
  getMeta: PropTypes.func,
  form: PropTypes.any,
  viewMode: PropTypes.bool,
  initialValues: PropTypes.object,
  disabled: PropTypes.bool
};

export default FormBuilder;
